import actions from './actions';
import getters from './getters';
import mutations from './mutations';
import { HDRecordingStatus, MediaRoomState, ProgressiveCloudProxyStatus, SDRecordingStatus } from './types';

import { appMode, mobileCheck } from '@/assets/utilfunctions';
import { LOCAL_STORAGE_KEYS } from '@/constants';
import { DenoiserLevel } from '@/services/denoiser/constants';
import { isBrowserChrome } from '@/services/browser.service';
import { getValueFromLocalstore } from '@/services/localStorage.service';

export const defaultState = (): MediaRoomState => {
  const isMobile = mobileCheck();
  const isLiveStreamMode = appMode === 'liveStream';
  const allowVirtualBackgrounds = !isMobile && !isLiveStreamMode && (isBrowserChrome ? isBrowserChrome() : false);
  const getUserBackgroundPreferences = () => {
    if (!allowVirtualBackgrounds) {
      return {
        blur: false,
        image: false
      };
    }
    return getValueFromLocalstore(LOCAL_STORAGE_KEYS.userVirtualBgSpec, {
      image: true,
      blur: false
    });
  };

  const getUserNoiseSuppressionPreference = () => {
    return getValueFromLocalstore(LOCAL_STORAGE_KEYS.noiseCancellationLevel, DenoiserLevel.SOFT);
  };

  const isUserVideoMirrored = getValueFromLocalstore(LOCAL_STORAGE_KEYS.isUserVideoMirrored, true);
  return {
    mediaRooms: {},
    agoraTokens: [],
    localTracks: [],
    screenTracks: [],
    localAudioLevel: 0,
    signalMetadata: {
      networkQuality: {
        uplinkNetworkQuality: 0,
        downlinkNetworkQuality: 0
      },
      strength: 5
    },
    remoteSignalMetadata: {},
    stageControls: {
      video: true,
      audio: true
    },
    currentDevices: {
      camera: getValueFromLocalstore(`${appMode}Camera`, undefined),
      microphone: getValueFromLocalstore(`${appMode}Microphone`, undefined),
      speaker:
        window.mobileCheck && window.mobileCheck() ? undefined : getValueFromLocalstore(`${appMode}Speaker`, undefined)
    },
    rtcDeviceAccessError: {
      camera: false,
      microphone: false,
      speaker: false
    },
    rtcDeviceBrowserPermission: {
      camera: true,
      microphone: true,
      speaker: true
    },
    rtcDeviceSystemPermission: {
      camera: true,
      microphone: true,
      speaker: true
    },
    rtcDevices: {
      microphones: [],
      cameras: [],
      speakers: []
    },
    networkErrorIndex: undefined,
    userBackgroundFilterPreference: getUserBackgroundPreferences(),
    currentBackgroundFilter: '',
    allowVirtualBackgrounds,
    mirrorSelf: isUserVideoMirrored,
    showMuteNotifications: false,
    didEstablishConnection: false,
    // Start optimistic; will be updated when Agora SDK loads and checks compatibility
    noiseCancellationCompatible: true,
    selectedNoiseSuppressionLevel: getUserNoiseSuppressionPreference(),
    noiseCancellationEnabled: getValueFromLocalstore(LOCAL_STORAGE_KEYS.noiseCancellationEnabled, true),

    hdRecordingStatus: {
      user: HDRecordingStatus.IDLE,
      screenshare: HDRecordingStatus.IDLE
    },

    sdRecordingStatus: {
      user: SDRecordingStatus.IDLE,
      screenshare: SDRecordingStatus.IDLE
    },

    channelPeerConnectionStates: {}
  };
};

const state = defaultState();

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
};
